import { AsyncButton } from "aic";
import React, { ReactNode, useMemo } from "react";
import { TrashFill } from "react-bootstrap-icons";
import { CommentViewContext } from "./interface";
import { useComments } from "./useComments";

interface DeleteCommentButtonProps {
  activeContext: CommentViewContext;
  commentId: string;
  canEdit: boolean;
}

/**
 * Button to delete a comment if the user has permission and no replies exist.
 *
 * @param props - Destructured props object
 * @param props.activeContext - Active comment context
 * @param props.commentId - Id of the comment to delete
 * @param props.canEdit - When true user has permission to delete
 */
export const DeleteCommentButton = ({
  activeContext,
  commentId,
  canEdit,
}: DeleteCommentButtonProps): ReactNode => {
  const { deleteComment, comments, readComment, deleteRequest } = useComments(activeContext);

  const thisComment = readComment(commentId);

  /**
   * Handle click event to delete the comment.
   *
   * @param e - Mouse event
   */
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    deleteComment(commentId);
  };

  const disabled = useMemo(
    () =>
      !thisComment?.canDelete ||
      !canEdit ||
      comments.filter((c) => c.parentId === thisComment.id).length > 0,
    [thisComment, canEdit, comments],
  );

  return (
    thisComment && (
      <AsyncButton
        size="sm"
        aria-label="Delete comment"
        disabled={disabled}
        id={`delete-comment-${thisComment.id}`}
        className="align-self-top"
        onClick={handleClick}
        variant="danger"
        requestStatus={deleteRequest}
        title={disabled ? "Comment cannot be deleted" : "Delete comment"}
      >
        <TrashFill size={14} />
      </AsyncButton>
    )
  );
};

DeleteCommentButton.displayName = "DeleteCommentButton";
